import { Controller } from "@hotwired/stimulus";
import * as THREE from "three";

const CAMERA_DEFAULT = { x: 0, y: 6, z: -25 };
const CAMERA_BIRDS_EYE = { x: 0, y: 12, z: -12 };
const SIDE_VIEW_DISTANCE = 23;
const SIDE_VIEW_Y = 3;
const LERP_FACTOR = 0.08;
const CAMERA_DISTANCE = Math.sqrt(CAMERA_DEFAULT.x ** 2 + CAMERA_DEFAULT.y ** 2 + CAMERA_DEFAULT.z ** 2);
const MOUSE_SNAP_BACK_DELAY = 5000;

export default class extends Controller {
  static targets = ["canvas", "azimuth", "declination"];
  static values = { labels: Object };

  connect() {
    this.cameraGoal = { ...CAMERA_DEFAULT };
    this.labelsOpacityGoal = 1;
    this.render(this.canvasTarget);
  }

  azimuthFocus() {
    this.cameraGoal = { ...CAMERA_BIRDS_EYE };
  }

  azimuthBlur() {
    this.cameraGoal = { ...CAMERA_DEFAULT };
  }

  declinationFocus() {
    const azimuthRad = (this.azimuthTarget.value * Math.PI) / 180;
    const cameraAngle = Math.atan2(this.camera.position.x, -this.camera.position.z);
    const optionA = azimuthRad + Math.PI / 2;
    const optionB = azimuthRad - Math.PI / 2;
    const diffA = Math.abs(Math.atan2(Math.sin(optionA - cameraAngle), Math.cos(optionA - cameraAngle)));
    const diffB = Math.abs(Math.atan2(Math.sin(optionB - cameraAngle), Math.cos(optionB - cameraAngle)));
    const sideAngle = diffA <= diffB ? optionA : optionB;
    this.cameraGoal = {
      x: SIDE_VIEW_DISTANCE * Math.sin(sideAngle),
      y: SIDE_VIEW_Y,
      z: -SIDE_VIEW_DISTANCE * Math.cos(sideAngle),
    };
    this.labelsOpacityGoal = 0;
  }

  declinationBlur() {
    this.cameraGoal = { ...CAMERA_DEFAULT };
    this.labelsOpacityGoal = 1;
  }

  render(app_container) {
    this.shadow_quality = 2048;

    this.mount = document.createElement("div");
    this.mount.setAttribute("style", "width: 100%; height: 220px;");

    app_container.appendChild(this.mount);

    this.scene = new THREE.Scene();

    this.width = 16;
    this.height = this.width;

    this.ratio = this.mount.offsetWidth / this.mount.offsetHeight;

    if (this.ratio < 1.7) {
      this.height /= this.ratio;
    } else {
      this.width *= this.ratio / 1.7;
      this.height /= 1.7;
    }

    this.camera = new THREE.PerspectiveCamera(30, this.width / this.height);

    this.camera.position.z = CAMERA_DEFAULT.z;
    this.camera.position.y = CAMERA_DEFAULT.y;
    this.camera.lookAt(0, 0, 0);
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    this.renderer.setPixelRatio(Math.max(window.devicePixelRatio, 2) * 2);
    this.renderer.shadowMap.type = THREE.VSMShadowMap;
    this.renderer.shadowMap.enabled = true;
    this.renderer.setSize(this.mount.offsetWidth, this.mount.offsetHeight);
    this.renderer.setClearColor(0x000000, 0);

    this.mouse = new THREE.Vector2();
    this.mouse.x = 1;
    this.mouse.y = 1;
    this.raycaster = new THREE.Raycaster();

    this.lights_group = this.addLights();
    this.scene.add(this.lights_group);

    this.mount.appendChild(this.renderer.domElement);

    this.addModels();
    this.addCompassLabels();
    this.setupMouseDrag();
    this.gameLoop();
  }

  setupMouseDrag() {
    this.isDragging = false;
    this.cameraAngle = Math.atan2(CAMERA_DEFAULT.x, -CAMERA_DEFAULT.z);
    this.cameraElevation = Math.asin(CAMERA_DEFAULT.y / CAMERA_DISTANCE);

    const onPointerDown = (e) => {
      this.isDragging = true;
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.dragStartAngle = this.cameraAngle;
      this.dragStartElevation = this.cameraElevation;
      clearTimeout(this.snapBackTimer);
    };

    const onPointerMove = (e) => {
      if (!this.isDragging) return;
      const dx = e.clientX - this.dragStartX;
      const dy = e.clientY - this.dragStartY;
      this.cameraAngle = this.dragStartAngle + dx * 0.02;
      this.cameraElevation = Math.max(0.1, Math.min(1.2, this.dragStartElevation + dy * 0.01));
      this.cameraGoal = {
        x: CAMERA_DISTANCE * Math.sin(this.cameraAngle) * Math.cos(this.cameraElevation),
        y: CAMERA_DISTANCE * Math.sin(this.cameraElevation),
        z: -CAMERA_DISTANCE * Math.cos(this.cameraAngle) * Math.cos(this.cameraElevation),
      };
    };

    const onPointerUp = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.snapBackTimer = setTimeout(() => {
        this.cameraGoal = { ...CAMERA_DEFAULT };
        this.cameraAngle = Math.atan2(CAMERA_DEFAULT.x, -CAMERA_DEFAULT.z);
        this.cameraElevation = Math.asin(CAMERA_DEFAULT.y / CAMERA_DISTANCE);
      }, MOUSE_SNAP_BACK_DELAY);
    };

    this.renderer.domElement.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);

    this.cleanupMouseDrag = () => {
      this.renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      clearTimeout(this.snapBackTimer);
    };
  }

  addModels() {
    this.ground_diameter = 10;
    this.ground_radius = this.ground_diameter / 2;
    this.ground_height = 0.7;

    this.solar_panel_size = new THREE.Vector3(7, 0.2, 5.5);

    this.solar_panel_gap = 0.2;

    this.root_group = new THREE.Group();
    this.ground_group = new THREE.Group();
    this.solar_panel_group = new THREE.Group();

    this.ground_bottom_geometry = new THREE.CylinderGeometry(
      this.ground_radius * 0.95,
      this.ground_radius * 0.95,
      this.ground_height,
      128,
      32,
      true,
    );
    this.ground_bottom_material = new THREE.MeshStandardMaterial({ side: THREE.DoubleSide, color: 0x9c3065 });
    this.ground_bottom = new THREE.Mesh(this.ground_bottom_geometry, this.ground_bottom_material);

    this.ground_bottom.position.y = -this.ground_height;

    this.ground_top_geometry = new THREE.CylinderGeometry(
      this.ground_radius,
      this.ground_radius,
      this.ground_height,
      128,
      32,
    );
    this.ground_top_material = new THREE.MeshStandardMaterial({ side: THREE.DoubleSide, color: 0xf66233 });
    this.ground_top = new THREE.Mesh(this.ground_top_geometry, this.ground_top_material);
    this.ground_top.receiveShadow = true;

    this.ground_group.add(this.ground_bottom);
    this.ground_group.add(this.ground_top);

    this.solar_panel_box_geometry = new THREE.BoxGeometry(
      this.solar_panel_size.x,
      this.solar_panel_size.y,
      this.solar_panel_size.z,
    );
    this.solar_panel_box_material = new THREE.MeshStandardMaterial({ side: THREE.DoubleSide, color: 0xffffff });
    this.solar_panel_box = new THREE.Mesh(this.solar_panel_box_geometry, this.solar_panel_box_material);
    this.solar_panel_box.castShadow = true;
    this.solar_panel_group.add(this.solar_panel_box);

    this.solar_panel_black_geometry = new THREE.PlaneGeometry(this.solar_panel_size.x, this.solar_panel_size.z);
    this.solar_panel_black_material = new THREE.MeshStandardMaterial({ side: THREE.DoubleSide, color: 0x322644 });
    this.solar_panel_black = new THREE.Mesh(this.solar_panel_black_geometry, this.solar_panel_black_material);
    this.solar_panel_black.rotation.x = Math.PI / 2;
    this.solar_panel_black.position.y = this.solar_panel_size.y / 2 + 0.01;
    this.solar_panel_black.scale.x = 0.95;
    this.solar_panel_black.scale.y = 0.95;
    this.solar_panel_group.add(this.solar_panel_black);

    this.solar_panel_lines_group = new THREE.Group();

    for (var i = 0; i < 4; i++) {
      var line_geometry = new THREE.PlaneGeometry(this.solar_panel_size.x, this.solar_panel_size.z);
      var line_material = new THREE.MeshStandardMaterial({ side: THREE.DoubleSide, color: 0x504f62 });
      var line = new THREE.Mesh(line_geometry, line_material);
      line.rotation.x = Math.PI / 2;
      line.position.x = (this.solar_panel_size.x / 5) * (i + 1) - this.solar_panel_size.x / 2;
      line.position.y = this.solar_panel_size.y / 2 + 0.02;
      line.scale.x = 0.007;
      line.scale.y = 0.7;
      this.solar_panel_lines_group.add(line);
    }

    for (var i = 0; i < 2; i++) {
      var line_geometry = new THREE.PlaneGeometry(this.solar_panel_size.x, this.solar_panel_size.z);
      var line_material = new THREE.MeshStandardMaterial({ side: THREE.DoubleSide, color: 0x504f62 });
      var line = new THREE.Mesh(line_geometry, line_material);
      line.rotation.x = Math.PI / 2;
      line.position.z = (this.solar_panel_size.x / 5) * (i + 1.5) - this.solar_panel_size.z / 2;
      line.position.y = this.solar_panel_size.y / 2 + 0.02;
      line.scale.y = 0.007;
      line.scale.x = 0.8;
      this.solar_panel_lines_group.add(line);
    }

    this.solar_panel_group.add(this.solar_panel_lines_group);

    this.solar_panel_group.position.y = this.ground_height / 2 + this.solar_panel_size.y + this.solar_panel_gap;

    this.root_group.add(this.ground_group);
    this.root_group.add(this.solar_panel_group);
    this.scene.add(this.root_group);

    this.root_group.position.y = -1.5;
  }

  addCompassLabels() {
    const textColor = this.getTextColor();
    const sideOffset = this.ground_radius + 1.5;
    const frontBackOffset = this.ground_radius + 1.2;
    const groundY = this.root_group.position.y;
    const labels = this.labelsValue;

    this.compassSprites = [
      this.createTextSprite(labels.e, textColor, -sideOffset, groundY, 0),
      this.createTextSprite(labels.w, textColor, sideOffset, groundY, 0),
      this.createTextSprite(labels.n, textColor, 0, groundY, frontBackOffset),
      this.createTextSprite(labels.s, textColor, 0, groundY, -frontBackOffset),
    ];
    this.compassSprites.forEach((sprite) => this.scene.add(sprite));
  }

  createTextSprite(text, color, x, y, z) {
    const canvas = document.createElement("canvas");
    const size = 128;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");

    ctx.font = "bold 72px Inter, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = color;
    ctx.fillText(text, size / 2, size / 2);

    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(1.5, 1.5, 1);
    sprite.position.set(x, y, z);
    return sprite;
  }

  getTextColor() {
    const style = getComputedStyle(document.documentElement);
    return style.getPropertyValue("--color-text-secondary").trim() || "#545566";
  }

  setAngle(angle_deg) {
    const angle_rad = (angle_deg * Math.PI) / 180;
    this.root_group.rotation.y = Math.PI - angle_rad;
  }

  setPanelAngle(angle_deg) {
    const angle_rad = (angle_deg * Math.PI) / 180;
    this.solar_panel_group.rotation.x = angle_rad;
    this.solar_panel_group.position.y =
      this.ground_height / 2 +
      this.solar_panel_size.y +
      this.solar_panel_gap +
      (Math.sin(angle_rad) * this.solar_panel_size.z) / 2;
  }

  disconnect() {
    cancelAnimationFrame(this.animationFrameId);
    if (this.cleanupMouseDrag) this.cleanupMouseDrag();
  }

  gameLoop() {
    this.animationFrameId = requestAnimationFrame(() => this.gameLoop());
    this.update();
    this.renderScene();
  }

  update() {
    this.setAngle(this.azimuthTarget.value);
    this.setPanelAngle(this.declinationTarget.value);
    this.updateCamera();
  }

  updateCamera() {
    this.camera.position.x += (this.cameraGoal.x - this.camera.position.x) * LERP_FACTOR;
    this.camera.position.y += (this.cameraGoal.y - this.camera.position.y) * LERP_FACTOR;
    this.camera.position.z += (this.cameraGoal.z - this.camera.position.z) * LERP_FACTOR;
    this.camera.lookAt(0, 0, 0);

    this.compassSprites.forEach((sprite) => {
      sprite.material.opacity += (this.labelsOpacityGoal - sprite.material.opacity) * LERP_FACTOR;
    });
  }

  renderScene() {
    this.renderer.render(this.scene, this.camera);
  }

  addLights() {
    var lights_group = new THREE.Group();

    var color = 0xffffff;
    var distance = 100;

    var lights_properties = [
      [0, distance, 0, 0.6, true],
      [0, 0, distance, 0.4, false],
    ];

    for (var i = 0; i < lights_properties.length; i++) {
      var light = new THREE.DirectionalLight(color, lights_properties[i][3], 500);
      var light_size = 10;

      light.shadow.camera.left = -light_size;
      light.shadow.camera.right = light_size;
      light.shadow.camera.top = light_size;
      light.shadow.camera.bottom = -light_size;
      light.shadow.mapSize.width = this.shadow_quality;
      light.shadow.mapSize.height = this.shadow_quality;
      light.shadow.radius = 32;
      light.shadow.blurSamples = 16;

      light.position.set(lights_properties[i][0], lights_properties[i][1], lights_properties[i][2]);
      if (lights_properties[i][4]) {
        light.castShadow = true;
      }

      lights_group.add(light);
    }

    const ambient_light = new THREE.AmbientLight(0xffffff, 0.8);
    lights_group.add(ambient_light);

    return lights_group;
  }
}
